import {
  chemkitClientIdHeader as contractsChemkitClientIdHeader,
  getOrCreateClientId,
} from '@chemkit/contracts/integration/clientIdentity';
import {
  CHEMKIT_STALE_WARNING_BATCH_EVENT,
  CHEMKIT_STALE_WARNING_BATCH_VERSION,
} from '@chemkit/contracts/integration/staleWarnings';

export const isChemkitStaleNotificationId = (value) => (
  typeof value === 'string' && /^ck-.+__(?:reaction|sample)__\d+__\d+$/.test(value)
);

export const getPresenceClientId = () => getOrCreateClientId(
  typeof window !== 'undefined' ? window.sessionStorage : null,
);

export const chemkitClientIdHeader = () => contractsChemkitClientIdHeader(
  typeof window !== 'undefined' ? window.sessionStorage : null,
);

export const filterMessagesForClient = (messages) => {
  const clientId = getPresenceClientId();
  if (!clientId) {
    return messages;
  }

  return messages.filter((message) => {
    const targetClientId = message?.content?.target_client_id;
    if (!targetClientId) {
      return true;
    }

    return targetClientId === clientId;
  });
};

export const isChemkitAutosaveChangedAction = (action) => action === 'ChemkitAutosaveChanged';

export const isChemkitReviewChangedAction = (action) => action === 'ChemkitReviewChanged';

export const isChemkitPresenceAction = (action) => (
  isChemkitAutosaveChangedAction(action) || isChemkitReviewChangedAction(action)
);

export const publishStaleWarnings = (response) => {
  try {
    const clientId = getPresenceClientId();
    const messages = Array.isArray(response?.messages) ? response.messages : [];
    const warnings = [];

    messages.forEach((message) => {
      const content = message?.content;
      const action = content?.action || content?.contentType;
      if (!isChemkitAutosaveChangedAction(action)) return;

      const targetClientId = content?.target_client_id;
      if (targetClientId && (!clientId || targetClientId !== clientId)) return;

      const entityId = Number(content?.entity_id);
      const entityKind = content?.entity_kind;
      if (!isChemkitStaleNotificationId(message?.id)) return;
      if (entityKind !== 'reaction' && entityKind !== 'sample') return;
      if (!Number.isInteger(entityId) || entityId <= 0) return;

      warnings.push({
        notificationId: message.id,
        entityKind,
        entityId,
      });
    });

    if (warnings.length > 0) {
      window.dispatchEvent(new CustomEvent(CHEMKIT_STALE_WARNING_BATCH_EVENT, {
        detail: {
          version: CHEMKIT_STALE_WARNING_BATCH_VERSION,
          warnings,
        },
      }));
    }
  } catch {
    // Stale-warning enhancement must never interrupt Chemotion notifications.
  }

  return response;
};

function buildReactionSamples(api, reactionId) {
  if (!api?.domain?.reactions?.sampleInfo) {
    return [];
  }
  let results;
  try {
    results = api.domain.reactions.sampleInfo(reactionId);
  } catch {
    return [];
  }
  if (!Array.isArray(results)) {
    return [];
  }
  const samples = [];
  for (let i = 0; i < results.length; i += 1) {
    const entry = results[i];
    if (!entry) continue;
    const id = Number.parseInt(entry.id, 10);
    if (!Number.isFinite(id) || id <= 0) continue;
    samples.push({
      id,
      updatedAtMs: typeof entry.updatedAtMs === 'number' ? entry.updatedAtMs : null,
    });
  }
  return samples;
}

export const buildMessagesBody = () => {
  const clientId = getPresenceClientId();
  const api = window.chemkit;
  if (!api?.app?.tabs?.list) {
    return { entities: [] };
  }
  let panes;
  try {
    panes = api.app.tabs.list();
  } catch {
    return { entities: [] };
  }
  if (!Array.isArray(panes)) {
    return { entities: [] };
  }
  const entities = [];
  for (let i = 0; i < panes.length; i += 1) {
    const pane = panes[i];
    if (!pane) continue;
    if (pane.type !== 'sample' && pane.type !== 'reaction') continue;
    if (pane.isNew === true) continue;
    const id = Number(pane.id);
    if (!Number.isFinite(id) || id <= 0 || !Number.isInteger(id)) continue;
    const entity = {
      kind: pane.type,
      id,
      isActive: pane.isActive === true,
      updatedAtMs: typeof pane.updatedAtMs === 'number' ? pane.updatedAtMs : null,
    };
    if (pane.type === 'reaction') {
      entity.samples = buildReactionSamples(api, id);
      entity.reviewState = pane.reviewState || null;
    }
    entities.push(entity);
  }
  return { client_id: clientId, entities };
};
